export type ParsedGeoJsonCrs =
  | {
      readonly kind: 'canonical';
      readonly identifier: 'OGC:CRS84';
      readonly declaredName: string;
    }
  | {
      readonly kind: 'epsg';
      readonly identifier: `EPSG:${number}`;
      readonly code: number;
      readonly declaredName: string;
    };

export type GeoJsonCrsDeclarationResult =
  | { readonly ok: true; readonly value: ParsedGeoJsonCrs }
  | {
      readonly ok: false;
      readonly detectedName?: string;
      readonly reason: 'malformed' | 'unsupported-name';
    };

function epsg(codeText: string, declaredName: string): ParsedGeoJsonCrs | undefined {
  const code = Number(codeText);
  if (!Number.isSafeInteger(code) || code <= 0) return undefined;
  return { kind: 'epsg', identifier: `EPSG:${code}`, code, declaredName };
}

export function parseNamedGeoJsonCrs(name: string): ParsedGeoJsonCrs | undefined {
  const trimmed = name.trim();
  if (!trimmed) return undefined;

  const direct = /^EPSG\s*:\s*(\d+)$/i.exec(trimmed);
  if (direct?.[1]) return epsg(direct[1], trimmed);

  const urn = /^urn:ogc:def:crs:EPSG:[^:]*:(\d+)$/i.exec(trimmed);
  if (urn?.[1]) return epsg(urn[1], trimmed);

  const url = /^https?:\/\/www\.opengis\.net\/def\/crs\/EPSG\/[^/]+\/(\d+)\/?$/i.exec(trimmed);
  if (url?.[1]) return epsg(url[1], trimmed);

  if (
    /^OGC\s*:\s*CRS84$/i.test(trimmed) ||
    /^urn:ogc:def:crs:OGC:[^:]*:CRS84$/i.test(trimmed) ||
    /^https?:\/\/www\.opengis\.net\/def\/crs\/OGC\/[^/]+\/CRS84\/?$/i.test(trimmed)
  ) {
    return { kind: 'canonical', identifier: 'OGC:CRS84', declaredName: trimmed };
  }

  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseGeoJsonCrsDeclaration(value: unknown): GeoJsonCrsDeclarationResult {
  if (!isRecord(value) || typeof value.type !== 'string' || !isRecord(value.properties)) {
    return { ok: false, reason: 'malformed' };
  }

  if (value.type.trim().toLowerCase() !== 'name' || typeof value.properties.name !== 'string') {
    return { ok: false, reason: 'malformed' };
  }

  const declaredName = value.properties.name.trim();
  const parsed = parseNamedGeoJsonCrs(declaredName);
  return parsed
    ? { ok: true, value: parsed }
    : {
        ok: false,
        reason: declaredName ? 'unsupported-name' : 'malformed',
        ...(declaredName ? { detectedName: declaredName } : {}),
      };
}
